/**
 * AdminBlogCrud — full blog article management for the admin panel.
 *
 * Features:
 *  - Article list with status badges, view/like counts, publish date, actions
 *  - Full-page create/edit form with TipTap WYSIWYG editor
 *  - Author auto-populated from current admin; manually overridable
 *  - Publish date, update date tracking
 *  - Comments enabled / show view count toggles per article
 *  - Embed code panel for pasting into any CMS page
 *  - Unpublish action, delete with confirmation
 *  - Comment moderation sub-view per article
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Check,
  ArrowLeft,
  Eye,
  EyeOff,
  Heart,
  MessageSquare,
  Calendar,
  X,
  BookOpen,
  ChevronDown,
  Sparkles,
  Search,
  AlertCircle,
  Wand2,
  Star,
} from 'lucide-react';
import {
  getAllBlogArticles,
  createBlogArticle,
  updateBlogArticle,
  deleteBlogArticle,
  getAllComments,
  updateCommentStatus,
  deleteComment,
  isSlugTaken,
  slugify,
  formatDate,
  BLOG_ARTICLES_EMBED_CODE,
  BLOG_FEATURED_ARTICLES_EMBED_CODE,
  type BlogArticle,
  type BlogArticlePayload,
  type BlogArticleStatus,
  type BlogComment,
} from '@/lib/blog';
import { BlogWysiwygEditor } from '@/components/BlogWysiwygEditor';
import { getAIConfigForWorkflow, getAIWorkflowAssignments } from '@/lib/ai-config';
import { getAIConfig, type AIProvider } from '@/lib/cms';
import { getAllBusinessContextItems, buildBusinessContextText, buildShortBusinessSummary } from '@/lib/business-context';
import { generateBlogArticleWithAI, generateLuckyArticleContext, type WebSearchCitation } from '@/lib/ai-blog';

interface AdminBlogCrudProps {
  currentUserId: string;
  /** Display name of the current admin (used to pre-populate author). */
  currentUserName?: string;
  currentUserEmail?: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
  titleAction?: React.ReactNode;
}

type View = 'list' | 'create' | 'edit' | 'comments';

interface FormState {
  title: string;
  slug: string;
  body: string;
  excerpt: string;
  featuredImage: string;
  status: BlogArticleStatus;
  authorName: string;
  authorEmail: string;
  publishedAt: string; // ISO datetime-local string
  tags: string; // comma-separated
  commentsEnabled: boolean;
  showViewCount: boolean;
  metaDescription: string;
  featured: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  slug: '',
  body: '',
  excerpt: '',
  featuredImage: '',
  status: 'draft',
  authorName: '',
  authorEmail: '',
  publishedAt: '',
  tags: '',
  commentsEnabled: true,
  showViewCount: false,
  metaDescription: '',
  featured: false,
};

const STATUS_LABELS: Record<BlogArticleStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  unpublished: 'Unpublished',
};

const STATUS_COLORS: Record<BlogArticleStatus, string> = {
  published: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-700',
  unpublished: 'bg-gray-100 text-gray-500',
};

/** Allowed topics for "Make me lucky" — AI must choose exactly one. */
const LUCKY_TOPICS = [
  'Federal Subcontracting Strategy',
  'Prime Contractor Outreach',
  'BD Process & Systems',
  'Small Business Growth',
  'NAICS Alignment',
  'Meeting Preparation',
] as const;

/** Pick a random topic from LUCKY_TOPICS for "Make me lucky". */
function pickRandomLuckyTopic(): (typeof LUCKY_TOPICS)[number] {
  return LUCKY_TOPICS[Math.floor(Math.random() * LUCKY_TOPICS.length)];
}

/** Build lucky article context with a specific topic (for fallback when AI is not used). */
function buildLuckyContextWithTopic(
  topic: string,
  withBusinessPrefix: boolean
): string {
  const topicBlock = `Topic: ${topic}. Focus the entire article on this topic.`;
  const instruction = withBusinessPrefix
    ? `Using the business context above, write an Insights-style article: practical insights on federal subcontracting, Prime outreach, and structured growth.\n\n${topicBlock}`
    : `Write an Insights-style article: practical insights on federal subcontracting, Prime outreach, and structured growth.\n\n${topicBlock}`;
  const rest = `

Every article must deliver: (1) Practical Insights — real strategies readers can apply to their federal BD process immediately; (2) Growth Focus — structured, sustainable growth, not quick wins; (3) Clear Next Steps — a direct path forward (e.g. our process or a strategy call).

Target audience: SMBs ($1M–$50M), BD and capture roles, owners and executives. Tone: professional, authoritative, trusted advisor—never salesy.`;
  return instruction + rest;
}

const MAX_SEARCH_QUERY_LENGTH = 80;

/** Strip markdown and extra whitespace for search query. */
function stripForSearchQuery(text: string): string {
  return text
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Derive a short, keyword-friendly search query from article context. Prefers Topic line or known topic name; otherwise a clean truncation of the start. */
function deriveSearchQueryFromContext(context: string): string {
  const trimmed = context.trim();
  if (!trimmed) return '';

  const normalized = stripForSearchQuery(trimmed);

  // If the context mentions one of our known topics, use it as the query (best for web search).
  const mentionedTopic = LUCKY_TOPICS.find((t) =>
    normalized.toLowerCase().includes(t.toLowerCase())
  );
  if (mentionedTopic) return mentionedTopic;

  const lines = trimmed.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const topicLine = lines.find((l) => /^Topic:\s*/i.test(l));
  const source = topicLine ?? lines[0] ?? trimmed;

  let query = stripForSearchQuery(source)
    .replace(/^Topic:\s*/i, '')
    .replace(/\s*\[e\.g\.\s*([^\]]*)\]\s*/gi, (_, inner) => (inner?.trim() ? ` ${inner.trim()} ` : ' '))
    .replace(/\s+/g, ' ')
    .trim();

  if (query.length > MAX_SEARCH_QUERY_LENGTH) {
    const cut = query.slice(0, MAX_SEARCH_QUERY_LENGTH).trim();
    const lastSpace = cut.lastIndexOf(' ');
    query = lastSpace > 40 ? cut.slice(0, lastSpace) : cut;
  }
  return query;
}

export function AdminBlogCrud({
  currentUserId,
  currentUserName = '',
  currentUserEmail = '',
  titleAction,
  showNotify,
  confirmAction,
}: AdminBlogCrudProps): JSX.Element {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<View>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [copied, setCopied] = useState(false);
  const [copiedFeatured, setCopiedFeatured] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [commentsArticle, setCommentsArticle] = useState<BlogArticle | null>(null);
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    seo: false,
    options: true,
    aiContext: true,
  });

  // AI generation state
  const [articleContext, setArticleContext] = useState('');
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [generating, setGenerating] = useState(false);
  const [luckyLoading, setLuckyLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiConfigured, setAiConfigured] = useState(false);
  const [aiProvider, setAiProvider] = useState<AIProvider | null>(null);
  /** Web search citations from last generation (per websearch.md: visible and clickable). */
  const [generatedCitations, setGeneratedCitations] = useState<WebSearchCitation[]>([]);

  const notify = useCallback(
    (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
      showNotify?.(msg, type);
    },
    [showNotify]
  );

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllBlogArticles();
      setArticles(data);
    } catch (err) {
      console.error('[AdminBlogCrud] Failed to load articles:', err);
      notify('Failed to load articles', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function handleTitleChange(title: string) {
    setForm((prev) => ({
      ...prev,
      title,
      slug: !slugManuallyEdited ? slugify(title) : prev.slug,
    }));
    setFormErrors((prev) => ({ ...prev, title: undefined, slug: undefined }));
  }

  function validateForm(): boolean {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim()) errors.title = 'Title is required';
    if (!form.slug.trim()) errors.slug = 'Slug is required';
    if (!form.body.trim() || form.body === '<p></p>') errors.body = 'Body content is required';
    if (!form.authorName.trim()) errors.authorName = 'Author name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function articleToForm(article: BlogArticle): FormState {
    let publishedAt = '';
    if (article.publishedAt) {
      const d = article.publishedAt instanceof Date
        ? article.publishedAt
        : (article.publishedAt as { toDate: () => Date }).toDate?.() ?? new Date();
      // Format for datetime-local input: YYYY-MM-DDTHH:MM
      publishedAt = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
    }
    return {
      title: article.title,
      slug: article.slug,
      body: article.body,
      excerpt: article.excerpt ?? '',
      featuredImage: article.featuredImage ?? '',
      status: article.status,
      authorName: article.authorName,
      authorEmail: article.authorEmail ?? '',
      publishedAt,
      tags: (article.tags ?? []).join(', '),
      commentsEnabled: article.commentsEnabled ?? true,
      showViewCount: article.showViewCount ?? false,
      metaDescription: article.metaDescription ?? '',
      featured: article.featured ?? false,
    };
  }

  function formToPayload(): BlogArticlePayload {
    return {
      title: form.title.trim(),
      slug: form.slug.trim(),
      body: form.body,
      excerpt: form.excerpt.trim(),
      featuredImage: form.featuredImage.trim() || null,
      status: form.status,
      authorId: currentUserId,
      authorName: form.authorName.trim(),
      authorEmail: form.authorEmail.trim() || null,
      publishedAt: form.publishedAt ? new Date(form.publishedAt) : null,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      commentsEnabled: form.commentsEnabled,
      showViewCount: form.showViewCount,
      metaDescription: form.metaDescription.trim() || null,
      featured: form.featured,
    };
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  function goToList() {
    setView('list');
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSlugManuallyEdited(false);
    setGeneratedCitations([]);
  }

  function openCreate() {
    setForm({
      ...EMPTY_FORM,
      authorName: currentUserName,
      authorEmail: currentUserEmail,
    });
    setEditingId(null);
    setSlugManuallyEdited(false);
    setGeneratedCitations([]);
    setView('create');
  }

  function openEdit(article: BlogArticle) {
    setForm(articleToForm(article));
    setEditingId(article.id);
    setSlugManuallyEdited(true); // don't auto-update slug for existing articles
    setGeneratedCitations([]);
    setExpandedSections((prev) => ({ ...prev, aiContext: false }));
    setView('edit');
  }

  async function openComments(article: BlogArticle) {
    setCommentsArticle(article);
    setView('comments');
    setCommentsLoading(true);
    try {
      const data = await getAllComments(article.id);
      setComments(data);
    } catch (err) {
      console.error('[AdminBlogCrud] Failed to load comments:', err);
      notify('Failed to load comments', 'error');
    } finally {
      setCommentsLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = formToPayload();
      const slugConflict = await isSlugTaken(payload.slug, editingId ?? undefined);
      if (slugConflict) {
        setFormErrors((prev) => ({ ...prev, slug: 'This slug is already taken' }));
        notify('Slug is already taken by another article', 'error');
        setSaving(false);
        return;
      }
      if (editingId) {
        await updateBlogArticle(editingId, payload);
        notify('Article updated successfully', 'success');
        await loadArticles();
        // Stay on edit page after update; user can keep editing or navigate away
      } else {
        await createBlogArticle(payload);
        notify('Article created successfully', 'success');
        await loadArticles();
        goToList();
      }
    } catch (err) {
      console.error('[AdminBlogCrud] Failed to save article:', err);
      notify('Failed to save article', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(article: BlogArticle) {
    confirmAction?.(
      'Delete Article',
      `Are you sure you want to permanently delete "${article.title}"? This cannot be undone.`,
      async () => {
        try {
          await deleteBlogArticle(article.id);
          notify('Article deleted', 'success');
          await loadArticles();
          if (editingId === article.id) goToList();
        } catch (err) {
          console.error('[AdminBlogCrud] Failed to delete article:', err);
          notify('Failed to delete article', 'error');
        }
      }
    );
  }

  async function handleTogglePublish(article: BlogArticle) {
    const newStatus: BlogArticleStatus =
      article.status === 'published' ? 'unpublished' : 'published';
    try {
      await updateBlogArticle(article.id, { status: newStatus });
      notify(
        newStatus === 'published' ? 'Article published' : 'Article unpublished',
        'success'
      );
      await loadArticles();
    } catch (err) {
      console.error('[AdminBlogCrud] Failed to toggle publish status:', err);
      notify('Failed to update article status', 'error');
    }
  }

  // ---------------------------------------------------------------------------
  // Comment moderation
  // ---------------------------------------------------------------------------

  async function handleCommentStatus(
    comment: BlogComment,
    status: 'approved' | 'rejected'
  ) {
    if (!commentsArticle) return;
    try {
      await updateCommentStatus(commentsArticle.id, comment.id, status);
      setComments((prev) =>
        prev.map((c) => (c.id === comment.id ? { ...c, status } : c))
      );
      notify(`Comment ${status}`, 'success');
    } catch {
      notify('Failed to update comment', 'error');
    }
  }

  async function handleDeleteComment(comment: BlogComment) {
    if (!commentsArticle) return;
    confirmAction?.(
      'Delete Comment',
      'Are you sure you want to delete this comment?',
      async () => {
        try {
          await deleteComment(commentsArticle.id, comment.id);
          setComments((prev) => prev.filter((c) => c.id !== comment.id));
          notify('Comment deleted', 'success');
        } catch {
          notify('Failed to delete comment', 'error');
        }
      }
    );
  }

  // ---------------------------------------------------------------------------
  // AI config check (on create/edit open)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (view !== 'create' && view !== 'edit') return;
    let cancelled = false;
    getAIConfigForWorkflow('blog_writing').then((ai) => {
      if (cancelled) return;
      setAiConfigured(!!ai?.apiKey);
      setAiProvider(ai?.provider ?? null);
    }).catch(() => {
      if (!cancelled) setAiConfigured(false);
    });
    return () => { cancelled = true; };
  }, [view]);

  // ---------------------------------------------------------------------------
  // AI generation
  // ---------------------------------------------------------------------------

  const handleMakeMeLucky = useCallback(async () => {
    setLuckyLoading(true);
    setAiError('');
    const pickedTopic = pickRandomLuckyTopic();
    try {
      const [config, items] = await Promise.all([
        getAIConfigForWorkflow('article_context_suggestion'),
        getAllBusinessContextItems(),
      ]);
      // Prefer article_context_suggestion workflow model; if getAIConfigForWorkflow returned null
      // (e.g. keys only in legacy doc), merge assignment + legacy so "Make me lucky" uses assigned model.
      let resolvedConfig = config;
      if (!resolvedConfig) {
        const [legacy, assignments] = await Promise.all([
          getAIConfig(),
          getAIWorkflowAssignments(),
        ]);
        const assignment = assignments.article_context_suggestion;
        if (
          legacy?.apiKey?.trim() &&
          assignment?.provider &&
          assignment?.model &&
          legacy.provider === assignment.provider
        ) {
          resolvedConfig = {
            provider: assignment.provider,
            apiKey: legacy.apiKey.trim(),
            model: assignment.model,
          };
        }
      }
      if (!resolvedConfig) {
        resolvedConfig = await getAIConfigForWorkflow('blog_writing');
      }
      const fullContext = buildBusinessContextText(items);
      const businessContextSummary = fullContext.trim().slice(0, 1500);

      if (resolvedConfig?.apiKey) {
        const context = await generateLuckyArticleContext({
          config: resolvedConfig,
          businessContextSummary,
          topic: pickedTopic,
        });
        const trimmed = context?.trim();
        if (trimmed) {
          setArticleContext(trimmed);
          setSearchQuery(pickedTopic);
          return;
        }
      }

      const shortSummary = buildShortBusinessSummary(items, 600);
      const fallbackContext = shortSummary.trim()
        ? `Business context: ${shortSummary}\n\n${buildLuckyContextWithTopic(pickedTopic, true)}`
        : buildLuckyContextWithTopic(pickedTopic, false);
      setArticleContext(fallbackContext);
      setSearchQuery(pickedTopic);
      if (!resolvedConfig?.apiKey) {
        notify('AI not configured; using default template.', 'info');
      }
    } catch (err) {
      console.warn('[AdminBlogCrud] Make me lucky: AI or load failed, using default template.', err);
      notify('Could not generate suggestion; using default template.', 'info');
      const fallbackTopic = pickRandomLuckyTopic();
      const fallback = buildLuckyContextWithTopic(fallbackTopic, false);
      setArticleContext(fallback);
      setSearchQuery(fallbackTopic);
    } finally {
      setLuckyLoading(false);
    }
  }, [notify]);

  async function handleGenerate() {
    if (!articleContext.trim()) {
      setAiError('Please describe what the article should be about before generating.');
      return;
    }

    setGenerating(true);
    setAiError('');

    try {
      const [aiConfig, contextItems] = await Promise.all([
        getAIConfigForWorkflow('blog_writing'),
        getAllBusinessContextItems(),
      ]);

      if (!aiConfig) {
        setAiError('AI is not configured. Set up API keys and assign a model in System → LLM Models.');
        return;
      }

      const businessContextText = buildBusinessContextText(contextItems);

      const query = searchQuery.trim();
      const useSearch = searchEnabled && query;
      // Only OpenAI supports built-in web search (no separate API key)
      const openAIWebSearchQuery =
        aiConfig.provider === 'openai' && useSearch ? query : undefined;

      let generated;
      try {
        generated = await generateBlogArticleWithAI({
          config: aiConfig,
          articleContext: articleContext.trim(),
          businessContextText,
          openAIWebSearchQuery,
        });
      } catch (searchErr) {
        const msg = searchErr instanceof Error ? searchErr.message : String(searchErr);
        const isSearchModelUnavailable =
          msg.includes('403') ||
          msg.includes('model_not_found') ||
          msg.includes('gpt-4o-search-preview') ||
          msg.includes('gpt-5-mini') ||
          msg.includes('does not have access to model');

        if (
          isSearchModelUnavailable &&
          aiConfig.provider === 'openai' &&
          openAIWebSearchQuery
        ) {
          notify(
            "Web search isn't available for your OpenAI account; generating without it.",
            'info'
          );
          generated = await generateBlogArticleWithAI({
            config: aiConfig,
            articleContext: articleContext.trim(),
            businessContextText,
            openAIWebSearchQuery: undefined,
          });
        } else {
          throw searchErr;
        }
      }

      // Fill form with generated content
      setForm((prev) => {
        const newTitle = generated.title || prev.title;
        const newSlug = !slugManuallyEdited && generated.title
          ? slugify(generated.title)
          : prev.slug;
        return {
          ...prev,
          title: newTitle,
          slug: newSlug,
          excerpt: generated.excerpt || prev.excerpt,
          body: generated.body || prev.body,
          tags: generated.tags.length > 0 ? generated.tags.join(', ') : prev.tags,
          metaDescription: generated.excerpt
            ? generated.excerpt.slice(0, 160)
            : prev.metaDescription,
        };
      });
      setGeneratedCitations(generated.citations ?? []);

      notify('Article generated! Review and edit before publishing.', 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Generation failed';
      console.error('[AdminBlogCrud] AI generation error:', err);
      setAiError(msg);
    } finally {
      setGenerating(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Copy embed code
  // ---------------------------------------------------------------------------

  function handleCopy() {
    navigator.clipboard.writeText(BLOG_ARTICLES_EMBED_CODE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleCopyFeatured() {
    navigator.clipboard.writeText(BLOG_FEATURED_ARTICLES_EMBED_CODE).then(() => {
      setCopiedFeatured(true);
      setTimeout(() => setCopiedFeatured(false), 2000);
    });
  }

  // ---------------------------------------------------------------------------
  // Collapsible section toggle
  // ---------------------------------------------------------------------------

  function toggleSection(key: string) {
    if (key === 'aiContext' && editingId) return;
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  if (view === 'list') {
    return (
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Blog Articles</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Create and manage blog posts for the /blog page.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {titleAction}
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus size={16} />
              New Article
            </button>
          </div>
        </div>

        {/* Embed code panel */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1.5">
            Embed code
          </p>
          <p className="text-xs text-blue-600 mb-2">
            Paste this into any CMS page body to display the blog articles listing.
            Or use the shortcode <code className="bg-blue-100 px-1 rounded">{'{{blog-articles}}'}</code> in your page content.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-blue-200 rounded px-2 py-1.5 text-xs text-gray-700 overflow-x-auto whitespace-nowrap">
              {BLOG_ARTICLES_EMBED_CODE}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Copy embed code"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Featured articles embed code panel */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1.5">
            Featured articles embed
          </p>
          <p className="text-xs text-amber-700 mb-2">
            Paste this into any CMS page to show only featured articles.
            Or use the shortcode <code className="bg-amber-100 px-1 rounded">{'{{blog-articles-featured}}'}</code> in your page content.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5 text-xs text-gray-700 overflow-x-auto whitespace-nowrap">
              {BLOG_FEATURED_ARTICLES_EMBED_CODE}
            </code>
            <button
              type="button"
              onClick={handleCopyFeatured}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
              aria-label="Copy featured articles embed code"
            >
              {copiedFeatured ? <Check size={13} /> : <Copy size={13} />}
              {copiedFeatured ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Featured articles list (when we have articles) */}
        {!loading && articles.length > 0 && (() => {
          const featuredArticles = articles.filter((a) => a.featured === true);
          return featuredArticles.length > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden">
              <div className="px-4 py-3 border-b border-amber-200">
                <h2 className="text-sm font-semibold text-amber-900">Featured articles</h2>
                <p className="text-xs text-amber-700 mt-0.5">Articles marked as featured appear in the featured embed.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-amber-100 text-sm">
                  <thead>
                    <tr className="bg-amber-50/80">
                      <th className="px-4 py-2 text-left text-xs font-semibold text-amber-800 uppercase tracking-wide">Title</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-amber-800 uppercase tracking-wide whitespace-nowrap">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-amber-800 uppercase tracking-wide whitespace-nowrap">Published</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-amber-800 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100 bg-white">
                    {featuredArticles.map((article) => (
                      <tr key={article.id} className="hover:bg-amber-50/50 transition-colors">
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-900 truncate max-w-xs">{article.title}</div>
                          <div className="text-xs text-gray-500 truncate max-w-xs">/{article.slug}</div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[article.status]}`}>
                            {STATUS_LABELS[article.status]}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-500">
                          {article.publishedAt ? formatDate(article.publishedAt) : '—'}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-right">
                          <button
                            type="button"
                            title="Edit article"
                            onClick={() => openEdit(article)}
                            className="p-1.5 rounded text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <Pencil size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50/30 px-4 py-3">
              <p className="text-sm text-amber-800">No featured articles yet.</p>
              <p className="text-xs text-amber-700 mt-0.5">Check &quot;Feature this article&quot; in the article Options when editing to add articles here.</p>
            </div>
          );
        })()}

        {/* Article table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          </div>
        ) : articles.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-16 text-center">
            <BookOpen size={40} className="text-gray-300 mb-3" />
            <p className="font-medium text-gray-600">No articles yet</p>
            <p className="text-sm text-gray-400 mt-1">Create your first blog article to get started.</p>
            <button
              type="button"
              onClick={openCreate}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus size={15} />
              Create your first article
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    Featured
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Author
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    Published
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Stats
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {articles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-xs">
                        {article.title}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                        /{article.slug}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[article.status]}`}
                      >
                        {STATUS_LABELS[article.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {article.featured ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          <Star size={12} className="fill-amber-500 text-amber-500" />
                          Featured
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {article.authorName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {article.publishedAt ? formatDate(article.publishedAt) : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-3 text-gray-500">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Eye size={12} />
                          {article.viewCount ?? 0}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Heart size={12} />
                          {article.likeCount ?? 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => openComments(article)}
                          className="inline-flex items-center gap-1 text-xs hover:text-blue-600 transition-colors"
                          title="View comments"
                        >
                          <MessageSquare size={12} />
                          Comments
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        {/* Publish / Unpublish toggle */}
                        <button
                          type="button"
                          title={
                            article.status === 'published' ? 'Unpublish' : 'Publish'
                          }
                          onClick={() => handleTogglePublish(article)}
                          className={`p-1.5 rounded transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                            article.status === 'published'
                              ? 'text-green-600 hover:bg-red-50 hover:text-red-600'
                              : 'text-gray-400 hover:bg-green-50 hover:text-green-600'
                          }`}
                        >
                          {article.status === 'published' ? (
                            <EyeOff size={15} />
                          ) : (
                            <Eye size={15} />
                          )}
                        </button>
                        {/* Edit */}
                        <button
                          type="button"
                          title="Edit article"
                          onClick={() => openEdit(article)}
                          className="p-1.5 rounded text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <Pencil size={15} />
                        </button>
                        {/* Delete */}
                        <button
                          type="button"
                          title="Delete article"
                          onClick={() => handleDelete(article)}
                          className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors focus:outline-none focus:ring-1 focus:ring-red-500"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Comments view
  // ---------------------------------------------------------------------------

  if (view === 'comments' && commentsArticle) {
    const pending = comments.filter((c) => c.status === 'pending');
    const approved = comments.filter((c) => c.status === 'approved');
    const rejected = comments.filter((c) => c.status === 'rejected');

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goToList}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <ArrowLeft size={15} />
            Back to Articles
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <h2 className="text-base font-semibold text-gray-900">
            Comments — {commentsArticle.title}
          </h2>
        </div>

        {commentsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          </div>
        ) : comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-12 text-center">
            <MessageSquare size={36} className="text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No comments yet for this article.</p>
          </div>
        ) : (
          <>
            {[
              { label: 'Pending Review', items: pending, color: 'yellow' },
              { label: 'Approved', items: approved, color: 'green' },
              { label: 'Rejected', items: rejected, color: 'red' },
            ]
              .filter((g) => g.items.length > 0)
              .map((group) => (
                <div key={group.label}>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {group.label}{' '}
                    <span className="text-gray-400 font-normal">({group.items.length})</span>
                  </h3>
                  <div className="space-y-3">
                    {group.items.map((comment) => (
                      <div
                        key={comment.id}
                        className="rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-gray-900">
                              {comment.authorName}
                              {comment.authorEmail && (
                                <span className="ml-2 font-normal text-gray-400 text-xs">
                                  {comment.authorEmail}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {formatDate(comment.createdAt)}
                            </p>
                            <p className="text-sm text-gray-700 mt-2">{comment.body}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {comment.status !== 'approved' && (
                              <button
                                type="button"
                                onClick={() => handleCommentStatus(comment, 'approved')}
                                className="px-2.5 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                              >
                                Approve
                              </button>
                            )}
                            {comment.status !== 'rejected' && (
                              <button
                                type="button"
                                onClick={() => handleCommentStatus(comment, 'rejected')}
                                className="px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                              >
                                Reject
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteComment(comment)}
                              className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Delete comment"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Create / Edit form view (full-page fixed layout per admin-panel-view-navigation rule)
  // ---------------------------------------------------------------------------

  const isEditing = view === 'edit';

  return (
    <div className="fixed inset-0 left-0 lg:left-56 z-30 flex flex-col bg-white">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={goToList}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
          >
            <ArrowLeft size={15} />
            Back to Articles
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <h2 className="text-base font-semibold text-gray-900 truncate">
            {isEditing ? 'Edit Article' : 'New Article'}
          </h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent inline-block" />
            ) : null}
            {saving ? 'Saving…' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* Two-column body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left sidebar — metadata */}
        <aside className="w-72 shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto flex flex-col">
          <div className="p-5 flex flex-col gap-5">
            {/* Title */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Article title"
                className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  formErrors.title ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              {formErrors.title && (
                <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>
              )}
            </div>

            {/* Slug */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  setField('slug', e.target.value);
                }}
                placeholder="url-friendly-slug"
                className={`w-full rounded-lg border px-3 py-2 text-sm font-mono shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  formErrors.slug ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <p className="text-xs text-gray-400 mt-1">
                Public URL: /blog/{form.slug || '…'}
              </p>
              {formErrors.slug && (
                <p className="text-xs text-red-500 mt-1">{formErrors.slug}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setField('status', e.target.value as BlogArticleStatus)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
              </select>
            </div>

            {/* Publish date */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={12} />
                  Publish Date
                </span>
              </label>
              <input
                type="datetime-local"
                value={form.publishedAt}
                onChange={(e) => setField('publishedAt', e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                Leave blank to auto-set when published.
              </p>
            </div>

            {/* Author */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Author Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.authorName}
                onChange={(e) => setField('authorName', e.target.value)}
                placeholder="Author display name"
                className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  formErrors.authorName ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              {formErrors.authorName && (
                <p className="text-xs text-red-500 mt-1">{formErrors.authorName}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Author Email
              </label>
              <input
                type="email"
                value={form.authorEmail}
                onChange={(e) => setField('authorEmail', e.target.value)}
                placeholder="author@example.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Featured image */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Featured Image URL
              </label>
              <input
                type="url"
                value={form.featuredImage}
                onChange={(e) => setField('featuredImage', e.target.value)}
                placeholder="https://…"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {form.featuredImage && (
                <img
                  src={form.featuredImage}
                  alt="Preview"
                  className="mt-2 w-full h-24 object-cover rounded-md border border-gray-200"
                  onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                />
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Tags
              </label>
              <input
                type="text"
                value={form.tags}
                onChange={(e) => setField('tags', e.target.value)}
                placeholder="govcon, contracts, sba"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">Comma-separated</p>
              {form.tags && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.tags
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean)
                    .map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() =>
                            setField(
                              'tags',
                              form.tags
                                .split(',')
                                .map((t) => t.trim())
                                .filter((t) => t && t !== tag)
                                .join(', ')
                            )
                          }
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </div>

            {/* Options (collapsible) */}
            <div>
              <button
                type="button"
                onClick={() => toggleSection('options')}
                className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors focus:outline-none"
              >
                <span>Options</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${expandedSections.options ? 'rotate-180' : ''}`}
                />
              </button>
              {expandedSections.options && (
                <div className="mt-3 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.commentsEnabled}
                      onChange={(e) => setField('commentsEnabled', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Enable comments</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.showViewCount}
                      onChange={(e) => setField('showViewCount', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Show view count publicly</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.featured}
                      onChange={(e) => setField('featured', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Feature this article</span>
                  </label>
                </div>
              )}
            </div>

            {/* SEO (collapsible) */}
            <div>
              <button
                type="button"
                onClick={() => toggleSection('seo')}
                className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors focus:outline-none"
              >
                <span>SEO</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${expandedSections.seo ? 'rotate-180' : ''}`}
                />
              </button>
              {expandedSections.seo && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                      Meta Description
                    </label>
                    <textarea
                      value={form.metaDescription}
                      onChange={(e) => setField('metaDescription', e.target.value)}
                      placeholder="Brief description for search engines…"
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm resize-none focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {form.metaDescription.length}/160 chars recommended
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Danger zone — delete only when editing; separated from primary Update to avoid accidental clicks */}
          {isEditing && editingId && (() => {
            const article = articles.find((a) => a.id === editingId);
            return article ? (
              <div className="p-5 pt-4 mt-auto border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Danger zone
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Permanently delete this article. This cannot be undone.
                </p>
                <button
                  type="button"
                  onClick={() => handleDelete(article)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                  title="Delete article"
                >
                  <Trash2 size={14} />
                  Delete article
                </button>
              </div>
            ) : null;
          })()}
        </aside>

        {/* Right panel — main editor */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-y-auto">
          <div className="p-6 flex flex-col gap-6">
            {/* AI Context section — collapsed and disabled when editing existing article */}
            <div className={`rounded-xl border overflow-hidden ${editingId ? 'border-gray-200 bg-gray-50' : 'border-violet-200 bg-violet-50'}`}>
              <button
                type="button"
                onClick={() => toggleSection('aiContext')}
                disabled={!!editingId}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold transition-colors focus:outline-none ${
                  editingId
                    ? 'text-gray-500 cursor-not-allowed'
                    : 'text-violet-800 hover:bg-violet-100'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Sparkles size={15} className={editingId ? 'text-gray-400' : 'text-violet-600'} />
                  Generate with AI
                  {editingId && (
                    <span className="text-xs font-normal text-gray-400">
                      (available when creating a new article)
                    </span>
                  )}
                </span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${editingId ? 'text-gray-400' : 'text-violet-500'} ${expandedSections.aiContext ? 'rotate-180' : ''}`}
                />
              </button>

              {expandedSections.aiContext && (
                <div className="px-4 pb-4 space-y-4 border-t border-violet-200 pt-4">
                  {!aiConfigured ? (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                      <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        AI is not configured. Go to{' '}
                        <strong>Settings &amp; Branding → AI Content Generation</strong> to set up
                        your API key before using this feature.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Article context textarea */}
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <label className="text-xs font-semibold text-violet-700 uppercase tracking-wide">
                            Article context <span className="text-red-400">*</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => { void handleMakeMeLucky(); }}
                            disabled={luckyLoading}
                            className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Fill with a suggested context tied to GSGO's mission and programs"
                          >
                            {luckyLoading ? (
                              <span className="h-3 w-3 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
                            ) : (
                              <Wand2 size={12} />
                            )}
                            {luckyLoading ? 'Loading…' : 'Make me lucky'}
                          </button>
                        </div>
                        <textarea
                          value={articleContext}
                          onChange={(e) => {
                            const next = e.target.value;
                            setArticleContext(next);
                            setAiError('');
                            if (!searchQuery.trim()) {
                              setSearchQuery(deriveSearchQueryFromContext(next));
                            }
                          }}
                          placeholder="Describe what this article is about: topic, target audience, key points, angle, tone. Be specific for best results."
                          rows={4}
                          className="w-full rounded-lg border border-violet-300 bg-white px-3 py-2 text-sm shadow-sm resize-y focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                        />
                      </div>

                      {/* Search online (OpenAI only: uses built-in web search) */}
                      {aiProvider === 'openai' && (
                        <>
                          <label className="flex items-start gap-3 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={searchEnabled}
                              onChange={(e) => setSearchEnabled(e.target.checked)}
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-800">
                                Search online for verified information
                              </span>
                              <p className="text-xs text-violet-600 mt-0.5">
                                Uses OpenAI&apos;s built-in web search (no separate API key).
                              </p>
                            </div>
                          </label>

                          {searchEnabled && (
                        <div>
                          <label className="text-xs font-semibold text-violet-700 uppercase tracking-wide block mb-1">
                            Search query
                          </label>
                          <div className="relative">
                            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="e.g. SAM.gov registration requirements 2025"
                              className="w-full rounded-lg border border-violet-300 bg-white pl-7 pr-3 py-2 text-sm shadow-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Search results will be passed to the AI as verified context.
                          </p>
                        </div>
                          )}
                        </>
                      )}

                      {/* Error */}
                      {aiError && (
                        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                          <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                          <p className="text-xs text-red-700">{aiError}</p>
                        </div>
                      )}

                      {/* Generate button */}
                      <button
                        type="button"
                        onClick={() => { void handleGenerate(); }}
                        disabled={generating || !articleContext.trim()}
                        className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {generating ? (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <Sparkles size={14} />
                        )}
                        {generating ? 'Generating…' : 'Generate Article'}
                      </button>

                      <p className="text-xs text-violet-600">
                        Fills title, slug, excerpt, body, tags, and SEO meta description. Review and edit before publishing.
                        Articles follow the GSGO Insights standard: practical guidance for CKD patients, caregivers, and supporters; every article includes practical insights, impact focus, and clear next steps. Business context library is automatically included.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Excerpt / Summary
              </label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setField('excerpt', e.target.value)}
                placeholder="Short summary shown on the articles listing page…"
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm resize-none focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Body WYSIWYG */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Body <span className="text-red-500">*</span>
              </label>
              <BlogWysiwygEditor
                value={form.body}
                onChange={(html) => {
                  setField('body', html);
                }}
                placeholder="Write your article content here…"
              />
              {formErrors.body && (
                <p className="text-xs text-red-500 mt-1">{formErrors.body}</p>
              )}
            </div>

            {/* Web search sources (per websearch.md: citations visible and clickable) */}
            {generatedCitations.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                  Sources (from web search)
                </p>
                <ul className="space-y-1.5 text-sm">
                  {generatedCitations.map((c, i) => (
                    <li key={i}>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {c.title || c.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
